import { useState, useCallback } from 'react'
import { useSubscription } from './useSubscription'

export const useCreditWarning = () => {
  const [warningModal, setWarningModal] = useState({
    isOpen: false,
    actionType: null,
    creditsRequired: 0,
    creditsRemaining: 0,
    packageName: null,
    isFreeUser: false,
    message: null
  })

  const { canPerformAction } = useSubscription()

  // Check if action can be performed, show warning if not
  const checkAndWarnCredits = useCallback(async (actionType, retryCount = 0) => {
    try {
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Credit check timeout')), 3000)
      )
      
      const checkPromise = canPerformAction(actionType)
      const result = await Promise.race([checkPromise, timeoutPromise])
      
      // If subscription is still loading and we haven't retried too many times, wait and retry
      if (result.reason === 'no_subscription' && retryCount < 2) {
        await new Promise(resolve => setTimeout(resolve, 500))
        return checkAndWarnCredits(actionType, retryCount + 1)
      }
      
      // Double-check: if reason is insufficient_credits but user actually has enough credits, don't show warning
      if (result.reason === 'insufficient_credits' && result.creditsRemaining >= result.creditsRequired) {
        console.warn(`⚠️ False positive detected! User has ${result.creditsRemaining} credits, needs ${result.creditsRequired}. Allowing action.`)
        return true // Allow action despite the check saying insufficient
      }
      
      // Allow action if user is not authenticated (let auth handle it)
      if (result.reason === 'not_authenticated') {
        return true
      }
      
      // Only show warning for legitimate cases
      if (!result.canPerform && result.needsUpgrade) {
        setWarningModal({
          isOpen: true,
          actionType,
          creditsRequired: result.creditsRequired || 0,
          creditsRemaining: result.creditsRemaining || 0,
          packageName: result.packageName,
          isFreeUser: result.isFreeUser || false,
          message: result.message
        })
        return false // Cannot perform action
      }
      
      return true // Can perform action
    } catch (error) {
      console.warn('⚠️ Error checking credits, allowing action:', error.message)
      // Don't block the action if credit check fails
      return true
    }
  }, [canPerformAction])

  // Close warning modal
  const closeWarning = useCallback(() => {
    setWarningModal({
      isOpen: false,
      actionType: null,
      creditsRequired: 0,
      creditsRemaining: 0,
      packageName: null,
      isFreeUser: false,
      message: null
    })
  }, [])

  // Show custom warning
  const showCustomWarning = useCallback((options) => {
    setWarningModal({
      isOpen: true,
      actionType: options.actionType || 'unknown',
      creditsRequired: options.creditsRequired || 0,
      creditsRemaining: options.creditsRemaining || 0,
      packageName: options.packageName || null,
      isFreeUser: options.isFreeUser || false,
      message: options.message || null
    })
  }, [])

  return {
    warningModal,
    checkAndWarnCredits,
    closeWarning,
    showCustomWarning
  }
}

export default useCreditWarning